import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, generateReport, getReportsByGroup, Report, ToastService } from '@philips-emr/hpms-provider-commons';
import { ActionAPI, DeckAPI, DeckValue, FetchAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { MeetingMinuteDetailsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { MeetingMinute, MeetingMinutesValueStatus } from './meeting-minutes-controller';

const ATTACHMENTS_GROUP = '38';

@Controller({ fragment: FragmentNames.MEETING_MINUTE_DETAILS })
class MeetingMinuteDetailsController extends BaseController {

  @Inject(Aliases.PANEL_REFUSE)
  private panelRefuse: PanelAPI;
  @Inject(Aliases.DECK_ATTACHMENTS)
  private deckAttachments: DeckAPI;
  @Inject(Aliases.ACTION_ACCEPT)
  private actionAccept: ActionAPI;
  @Inject(Aliases.ACTION_REFUSE)
  private actionRefuse: ActionAPI;
  @Inject(Aliases.FETCH_MEETING_MINUTE_DETAILS)
  private fetchMeetingMinuteDetails: FetchAPI;

  @OnMount(Aliases.FETCH_MEETING_MINUTE_DETAILS)
  onMountFetchMeetingMinutesDetails(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProviderMeetingMinute } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqProviderMeetingMinute });
    api.forceFetch();
  }

  @OnMount(Aliases.DECK_ATTACHMENTS)
  async onMountFetchAttachments(event: MountEvent): Promise<void> {
    const api = event.target as DeckAPI;
    await getReportsByGroup(ATTACHMENTS_GROUP)
      .then(resp => {
        return api.setData(resp as unknown as DeckValue[]);
      });
  }

  @OnClick(Aliases.ACTION_REFUSE)
  onClickActionRefuse(): void {
    this.panelRefuse.setVisible(true);
  }

  @OnClose(Aliases.MODAL_REFUSE)
  onCloseModalRefuse(): void {
    this.panelRefuse.setVisible(false);
  }

  @OnCancel(Aliases.FORM_REFUSE)
  onCancelFormRefuse(): void {
    this.panelRefuse.setVisible(false);
  }

  @OnClick(Aliases.ACTION_ACCEPT)
  onClickActionAccept(): void {
    const { nrSeqProviderMeetingMinute } = this.historyService.getCurrentRouteParams();
    const { nrSeqProjMeetingMinute } = this.context.getBulkData();
    HttpService.post(Endpoint.POST.ACTION_MEETING_MINUTES, { nrSeqProviderMeetingMinute, nrSeqProjMeetingMinute })
      .then((response) => {
        ToastService.open(response as RequestResponse);
        this.fetchMeetingMinuteDetails.setParameters({ nrSeqProviderMeetingMinute });
        this.fetchMeetingMinuteDetails.forceFetch();
        this.actionAccept.setEnabled(false);
        this.actionRefuse.setEnabled(false);
      }).catch((error) => ToastService.open(error));
  }

  @OnSubmit(Aliases.FORM_REFUSE)
  onSubmitFormRefuse(event: SubmitEvent): void {
    const { messageRefuse } = event.record as MeetingMinute;
    const { nrSeqProviderMeetingMinute } = this.historyService.getCurrentRouteParams();
    const { nrSeqProjMeetingMinute } = this.context.getBulkData();
    HttpService.post(Endpoint.POST.ACTION_MEETING_MINUTES, { nrSeqProviderMeetingMinute, messageRefuse, nrSeqProjMeetingMinute })
      .then((response) => {
        ToastService.open(response as RequestResponse);
        this.panelRefuse.setVisible(false);
        this.fetchMeetingMinuteDetails.setParameters({ nrSeqProviderMeetingMinute });
        this.fetchMeetingMinuteDetails.forceFetch();
        this.actionAccept.setEnabled(false);
        this.actionRefuse.setEnabled(false);
      }).catch((error) => ToastService.open(error));
  }

  @OnAfterFetch(Aliases.FETCH_MEETING_MINUTE_DETAILS)
  onAfterFetchMeetingMinutesDetails(event: FetchEvent): void {
    const data = event.data;
    if (!isEmptyArrayOrObject(data)) {
      const { nrSeqProjMeetingMinute, iePending } = data as MeetingMinute;
      this.context.set({ nrSeqProjMeetingMinute });
      if (iePending === 'S') {
        this.actionAccept.setEnabled(true);
        this.actionRefuse.setEnabled(true);
      }
    }
  }

  @OnSelectItem(Aliases.DECK_ATTACHMENTS)
  onSelectAttachment(event: SelectItemEvent<Report>): void {
    const { nrSeqProviderMeetingMinute } = this.historyService.getCurrentRouteParams();
    const { nrSeqProjMeetingMinute } = this.context.getBulkData();

    const requestParams = {
      nrSeqProviderMeetingMinute,
      nrSeqProjMeetingMinute,
    };
    generateReport(Endpoint.POST.MEETING_MINUTES_REPORT, requestParams, ATTACHMENTS_GROUP, event.item)
      .finally(() => {
        this.changeCursor(false);
      });
    this.deckAttachments.unselectAll();
  }

  private changeCursor(isProgress: boolean): void {
    document.body.style.cursor = isProgress ? 'wait' : 'default';
  }

  @OnDynamicContentRender(Aliases.FIELD_STATUS)
  onRenderCardTask(event: RenderEvent): void {
    if (event.data) {
      const { data, render } = event;
      const { dsStatus, ieStatus } = data as MeetingMinute;
      if (ieStatus && dsStatus) {
        render(() : ReactElement  => getStampStatusMeetingMinutes(ieStatus, dsStatus));
      }
    }
  }
}

function getStampStatusMeetingMinutes(ieStatus: MeetingMinutesValueStatus, label: string): ReactElement {
  let stampProps = null;
  const icon = {
    [MeetingMinutesValueStatus.PENDING]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label },
    [MeetingMinutesValueStatus.ACCEPT]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [MeetingMinutesValueStatus.REFUSED]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label },
    [MeetingMinutesValueStatus.CANCELED]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label },
  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

export {
  getStampStatusMeetingMinutes,
  MeetingMinuteDetailsController,
};
